import { GraphQLError } from 'graphql'
import { useEffect } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'

import { ProfileData } from '../../../types'

import { FETCH_SESSION_SUPPLIERS } from '@/gf/queries/session'
import { VendorUserDocument, useUpdateUserProfileMutation } from '../../_gen/gql'

import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import Phone from '@/gf/modules/Phone'
import useVendorUser, { User } from '../../hooks/useVendorUser'

import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import Spinner from '@/gf/components/Spinner'
import TextField from '@/gf/components/TextField'
import useGqlClient from '@/suppliers/hooks/useGqlClient'

const ProfileInput = ({
  user,
  onProfileSubmit,
  spinnerLive,
}: {
  user: Pick<User, 'name' | 'email' | 'phoneNumber'>
  onProfileSubmit: SubmitHandler<ProfileData>
  spinnerLive
}) => {
  const profileForm = useForm<ProfileData>({
    shouldUnregister: true,
    defaultValues: {
      email: user.email,
      phoneNumber: user.phoneNumber ?? undefined,
      name: user.name,
    },
  })

  // Format/validate phone number
  const phoneNumber = useWatch({ control: profileForm.control, name: 'phoneNumber' })
  useEffect(() => {
    if (phoneNumber) profileForm.setValue('phoneNumber', Phone.format(phoneNumber))
  }, [phoneNumber])

  return (
    <Card title="Profile">
      <form className="space-y-4" onSubmit={profileForm.handleSubmit(onProfileSubmit)}>
        <Card.Section>
          <div className="space-y-4">
            <TextField label="Name" {...profileForm.register('name', { required: true })} />
            <TextField label="Email" {...profileForm.register('email', { required: true })} />
            <TextField label="Phone" {...profileForm.register('phoneNumber', { required: true })} />
          </div>
        </Card.Section>
        <div className="bg-gray-50 p-6 flex justify-end rounded-b-md">
          <Button type="submit" title="Save" performing={spinnerLive} />
        </div>
      </form>
    </Card>
  )
}

const Profile = ({ activeUser }: { activeUser: User }) => {
  const [_msgs, msgsMgr] = useMsgs()
  const gqlClient = useGqlClient()
  const [spinnerLive, spinnerToggle] = useToggle()

  const [updateUserProfile] = useUpdateUserProfileMutation({
    client: gqlClient,
    refetchQueries: [FETCH_SESSION_SUPPLIERS],
  })

  const onProfileSubmit: SubmitHandler<ProfileData> = (data: ProfileData) => {
    spinnerToggle.on()
    updateUserProfile({
      variables: {
        email: data.email,
        name: data.name,
        phoneNumber: data.phoneNumber ?? null,
        vendorId: null,
        vendorUserId: null,
      },
    })
      .then(() => {
        msgsMgr.add('Profile updated!', 'positive')
      })
      .catch((err: GraphQLError) => {
        msgsMgr.add(err.message, 'negative')
      })
      .finally(spinnerToggle.off)
  }

  return (
    <ProfileInput user={activeUser} onProfileSubmit={onProfileSubmit} spinnerLive={spinnerLive} />
  )
}

const VendorProfile = ({ vendorId, userId }: { vendorId: string; userId?: string }) => {
  const [_msgs, msgsMgr] = useMsgs()
  const gqlClient = useGqlClient()
  const [spinnerLive, spinnerToggle] = useToggle()
  const { data, error } = useVendorUser(vendorId, userId)
  const user = data?.vendorUser

  const [updateUserProfile] = useUpdateUserProfileMutation({
    client: gqlClient,
    refetchQueries: [VendorUserDocument],
  })

  const onProfileSubmit: SubmitHandler<ProfileData> = (form: ProfileData) => {
    spinnerToggle.on()
    updateUserProfile({
      variables: {
        email: form.email,
        name: form.name,
        phoneNumber: form.phoneNumber ?? null,
        vendorId,
        vendorUserId: userId ?? null,
      },
    })
      .then(() => {
        msgsMgr.add('Profile updated!', 'positive')
      })
      .catch((err: GraphQLError) => {
        msgsMgr.add(err.message, 'negative')
      })
      .finally(spinnerToggle.off)
  }

  return error ? (
    <p className="text-sm text-gray-900">Something went wrong, please contact support.</p>
  ) : !user ? (
    <Spinner />
  ) : (
    <ProfileInput user={user} onProfileSubmit={onProfileSubmit} spinnerLive={spinnerLive} />
  )
}

Profile.VendorProfile = VendorProfile
export default Profile
